import { itemsValue, logItems, type ItemAmount, type ItemValues, type LogLike } from './crimes-report';
import { type DateRange } from './log-kit';

/**
 * Torn's yearly events from the player's own log (website roadmap S16 — torn.report's Christmas Town, Easter Eggs and
 * Trick or Treat pages; Torn Intel's basket guide and energy-to-treats), on the log-report kit (S09). Pure and
 * browser-safe; tested in packages/shared/test/event-report.test.ts.
 *
 * Torn doesn't document its log lines' `data`, and these events run only a few weeks a year, so every reader is lenient:
 * a line belongs to an event by Torn's own category or title ("Christmas Town …", "Easter …", "Trick or treat …",
 * "Halloween …"), or by the item it names (Easter eggs, Halloween baskets — Torn's item ids). Money, items and treats come
 * from the usual field names; what can't be read is counted, not guessed. TO CHECK while each event runs: the titles and
 * fields of its lines (Christmas Town: December; Easter: around Easter; Trick or Treat: late October).
 */

export type TornEvent = 'christmas' | 'halloween' | 'easter';

export interface TornEventInfo {
  id: TornEvent;
  name: string;
  /** How Torn's calendar (v2 torn/calendar) names it. */
  calendar: RegExp;
  /** One line under the tab. */
  blurb: string;
}

/** In the order the website shows them: Christmas Town first (it's the next one, November → December). */
export const TORN_EVENTS: readonly TornEventInfo[] = [
  { id: 'christmas', name: 'Christmas Town', calendar: /christmas/i, blurb: 'What you found in Christmas Town — items, money, chests and games — valued at Torn’s market prices.' },
  { id: 'halloween', name: 'Trick or Treat', calendar: /trick\s*(or|'?n'?)\s*treat|halloween/i, blurb: 'Your treats and baskets: what each basket gave, treats a day, and — with your fights — treats per hit and per 100 energy.' },
  { id: 'easter', name: 'Easter eggs', calendar: /easter/i, blurb: 'The Easter eggs you found and used, by colour, every year.' },
];

export const eventInfo = (id: TornEvent): TornEventInfo => TORN_EVENTS.find((e) => e.id === id)!;

/** Torn's Easter egg items (Blue, Green, Red, Yellow, White, Black, Gold, Brown, Orange, Pink, Purple). */
export const EASTER_EGG_IDS: readonly number[] = [472, 473, 474, 475, 476, 477, 478, 583, 584, 585, 1149];
/** Torn's Halloween basket items. */
export const HALLOWEEN_BASKET_IDS: readonly number[] = [920, 1003, 1004, 1005, 1006, 1007, 1008, 1009, 1010, 1011];
/** Energy an attack costs (Torn's). */
export const EVENT_ATTACK_ENERGY = 25;

type Obj = Record<string, unknown>;
const obj = (x: unknown): Obj | null => (x && typeof x === 'object' && !Array.isArray(x) ? (x as Obj) : null);
const num = (x: unknown): number | null => {
  if (typeof x === 'number' && Number.isFinite(x)) return x;
  if (typeof x === 'string' && x.trim() !== '' && Number.isFinite(Number(x.replace(/[$,]/g, '')))) return Number(x.replace(/[$,]/g, ''));
  return null;
};
const firstOf = (d: Obj, keys: readonly string[]): unknown => {
  for (const k of keys) if (d[k] !== undefined && d[k] !== null && d[k] !== '') return d[k];
  return undefined;
};

const GAINED = ['items_gained', 'item_gained', 'items_found', 'item_found', 'items_received', 'item_received', 'rewards', 'reward_items', 'prizes', 'prize_items'];
const LOST = ['items_lost', 'item_lost', 'items_used', 'item_used', 'items_consumed'];
const MONEY = ['money_gained', 'money_found', 'money', 'cash_gained', 'cash', 'money_won'];
const TREATS = ['treats', 'treats_gained', 'treats_collected', 'treats_received', 'candy', 'candies'];

const eggs = new Set(EASTER_EGG_IDS);
const baskets = new Set(HALLOWEEN_BASKET_IDS);

/** The one item a line names in `data.item` (with its quantity), if any. */
function singleItem(d: Obj): ItemAmount | null {
  const id = num(d.item ?? d.item_id);
  if (id === null || id <= 0 || !Number.isInteger(id)) return null;
  return { id, amount: Math.max(1, num(firstOf(d, ['quantity', 'qty', 'amount'])) ?? 1) };
}

/** Which event a log line belongs to (null: none). */
export function eventOfLog(l: LogLike): TornEvent | null {
  const text = `${l.category ?? ''} ${l.title ?? ''}`.toLowerCase();
  if (/christmas/.test(text)) return 'christmas';
  if (/easter/.test(text)) return 'easter';
  if (/trick[\s-]*(or|'?n'?)[\s-]*treat|halloween/.test(text)) return 'halloween';
  const d = l.data ?? {};
  const ids = [singleItem(d), ...logItems(firstOf(d, GAINED))].filter((x): x is ItemAmount => !!x).map((x) => x.id);
  if (ids.some((id) => eggs.has(id))) return 'easter';
  if (ids.some((id) => baskets.has(id))) return 'halloween';
  return null;
}

export type EventLineKind = 'find' | 'open' | 'use' | 'attack' | 'buy' | 'game' | 'other';

export interface EventLine {
  id: string;
  at: number;
  event: TornEvent;
  kind: EventLineKind;
  title: string;
  money: number;
  gained: ItemAmount[];
  lost: ItemAmount[];
  /** Treats the line gives (Trick or Treat), null when it doesn't say. */
  treats: number | null;
}

/** What a line did, from its title. */
export function eventLineKind(title: string): EventLineKind {
  const t = title.toLowerCase();
  if (/\bopen/.test(t)) return 'open';
  if (/\battack|\bhit\b|\bmug|hospitali[sz]/.test(t)) return 'attack';
  if (/\b(buy|bought|purchase)/.test(t)) return 'buy';
  if (/\b(use|used|eat|eaten|consume)/.test(t)) return 'use';
  if (/\b(find|found|pick|picked|collect|receive|gather)/.test(t)) return 'find';
  if (/\b(game|wheel|spin|win|won|prize|play)/.test(t)) return 'game';
  return 'other';
}

/** A log line → what it says about an event (null: not an event line). */
export function readEventLine(l: LogLike): EventLine | null {
  const event = eventOfLog(l);
  if (!event) return null;
  const d = l.data ?? {};
  const kind = eventLineKind(l.title ?? '');
  const one = singleItem(d);
  let gained = logItems(firstOf(d, GAINED));
  let lost = logItems(firstOf(d, LOST));
  // A line naming one item: picked up (a find), or the item used/opened (a basket, an egg eaten).
  if (one && !gained.some((x) => x.id === one.id) && !lost.some((x) => x.id === one.id)) {
    if (kind === 'use' || kind === 'open') lost = [...lost, one];
    else if (kind !== 'buy') gained = [...gained, one];
  }
  const treats = num(firstOf(d, TREATS));
  return {
    id: l.id,
    at: l.at,
    event,
    kind,
    title: (l.title ?? '').trim() || l.category || 'Not named',
    money: Math.max(0, num(firstOf(d, MONEY)) ?? 0),
    gained,
    lost,
    treats: treats === null ? null : Math.max(0, treats),
  };
}

// ---------------------------------------------------------------- summing up

export interface EventItemRow {
  id: number;
  gained: number;
  lost: number;
  each: number | null;
  /** What was gained, at market value (null: no value known). */
  value: number | null;
}

export interface EventWhatRow {
  title: string;
  kind: EventLineKind;
  count: number;
  money: number;
  value: number;
}

export interface EventYearRow {
  year: number;
  lines: number;
  money: number;
  value: number;
  items: number;
  treats: number;
}

export interface EventSummary {
  lines: number;
  money: number;
  /** Items gained at market value. */
  value: number;
  /** Items gained (count). */
  items: number;
  /** Item kinds gained without a market value. */
  unvalued: number;
  treats: number;
  /** Lines that gave treats (a number). */
  treatLines: number;
  byItem: EventItemRow[];
  byWhat: EventWhatRow[];
  byYear: EventYearRow[];
}

export function summariseEvent(lines: readonly EventLine[], values: ItemValues): EventSummary {
  const items = new Map<number, EventItemRow>();
  const what = new Map<string, EventWhatRow>();
  const years = new Map<number, EventYearRow>();
  let money = 0;
  let value = 0;
  let count = 0;
  let treats = 0;
  let treatLines = 0;
  for (const l of lines) {
    const v = itemsValue(l.gained, values);
    const n = l.gained.reduce((a, i) => a + i.amount, 0);
    money += l.money;
    value += v;
    count += n;
    if (l.treats !== null) (treats += l.treats, treatLines++);
    for (const g of l.gained) {
      const r = items.get(g.id) ?? { id: g.id, gained: 0, lost: 0, each: values.get(g.id) ?? null, value: null };
      r.gained += g.amount;
      items.set(g.id, r);
    }
    for (const g of l.lost) {
      const r = items.get(g.id) ?? { id: g.id, gained: 0, lost: 0, each: values.get(g.id) ?? null, value: null };
      r.lost += g.amount;
      items.set(g.id, r);
    }
    const w = what.get(l.title) ?? { title: l.title, kind: l.kind, count: 0, money: 0, value: 0 };
    w.count++;
    w.money += l.money;
    w.value += v;
    what.set(l.title, w);
    const year = new Date(l.at).getUTCFullYear();
    const y = years.get(year) ?? { year, lines: 0, money: 0, value: 0, items: 0, treats: 0 };
    y.lines++;
    y.money += l.money;
    y.value += v;
    y.items += n;
    y.treats += l.treats ?? 0;
    years.set(year, y);
  }
  const byItem = [...items.values()].map((r) => ({ ...r, value: r.each === null ? null : r.each * r.gained }));
  byItem.sort((a, b) => (b.value ?? -1) - (a.value ?? -1) || b.gained - a.gained || a.id - b.id);
  return {
    lines: lines.length,
    money,
    value,
    items: count,
    unvalued: byItem.filter((r) => r.gained > 0 && r.each === null).length,
    treats,
    treatLines,
    byItem,
    byWhat: [...what.values()].sort((a, b) => b.count - a.count || a.title.localeCompare(b.title)),
    byYear: [...years.values()].sort((a, b) => b.year - a.year),
  };
}

/** The CSV of event lines. */
export function eventCsvRows(lines: readonly EventLine[]): { header: string[]; rows: Array<Array<string | number>> } {
  const items = (list: readonly ItemAmount[]) => list.map((i) => `${i.id}x${i.amount}`).join(' ');
  return {
    header: ['time_utc', 'event', 'what', 'kind', 'money', 'items_gained', 'items_lost', 'treats'],
    rows: lines.map((l) => [new Date(l.at).toISOString(), l.event, l.title, l.kind, l.money, items(l.gained), items(l.lost), l.treats ?? '']),
  };
}

// ---------------------------------------------------------------- Easter: eggs by colour

export interface EggRow {
  id: number;
  found: number;
  used: number;
}

/** Eggs found and used per egg item (any of Torn's egg ids seen in the lines), most found first. */
export function eggRows(lines: readonly EventLine[]): EggRow[] {
  const m = new Map<number, EggRow>();
  for (const l of lines) {
    for (const g of l.gained) if (eggs.has(g.id)) (m.get(g.id) ?? m.set(g.id, { id: g.id, found: 0, used: 0 }).get(g.id)!).found += g.amount;
    for (const g of l.lost) if (eggs.has(g.id)) (m.get(g.id) ?? m.set(g.id, { id: g.id, found: 0, used: 0 }).get(g.id)!).used += g.amount;
  }
  return [...m.values()].sort((a, b) => b.found - a.found || b.used - a.used || a.id - b.id);
}

/** Your all-time Easter eggs from personal stats (v2 `personalstats?cat=items`: items.found.easter_eggs / used), if Torn says. */
export function readEggTotals(body: unknown): { found: number | null; used: number | null } {
  const ps = obj(obj(body)?.personalstats);
  const items = obj(ps?.items);
  return { found: num(obj(items?.found)?.easter_eggs), used: num(obj(items?.used)?.easter_eggs) };
}

// ---------------------------------------------------------------- Trick or Treat: the basket guide and treat rates

export interface BasketRow {
  id: number;
  /** Times opened (or used) in the lines. */
  opened: number;
  /** Items those openings gave, at market value. */
  gotValue: number;
  gotItems: number;
  /** Money those openings gave. */
  gotMoney: number;
  /** Bought (from the lines). */
  bought: number;
}

/**
 * The basket guide from your own log: for each of Torn's Halloween baskets you opened or bought, how many and what the
 * openings gave (items at market value, money) — the page puts Torn's own name, description and market value beside it.
 */
export function basketRows(lines: readonly EventLine[], values: ItemValues): BasketRow[] {
  const m = new Map<number, BasketRow>();
  const row = (id: number) => m.get(id) ?? m.set(id, { id, opened: 0, gotValue: 0, gotItems: 0, gotMoney: 0, bought: 0 }).get(id)!;
  for (const l of lines) {
    const opened = l.lost.filter((i) => baskets.has(i.id));
    if (opened.length && (l.kind === 'open' || l.kind === 'use')) {
      const r = row(opened[0]!.id);
      r.opened += opened[0]!.amount;
      r.gotValue += itemsValue(l.gained, values);
      r.gotItems += l.gained.reduce((a, i) => a + i.amount, 0);
      r.gotMoney += l.money;
    }
    if (l.kind === 'buy') for (const g of [...l.gained]) if (baskets.has(g.id)) row(g.id).bought += g.amount;
  }
  return [...m.values()].sort((a, b) => b.opened - a.opened || a.id - b.id);
}

export interface TreatRates {
  treats: number;
  /** Your attacks in the range (Full: from Torn's fights; null without them). */
  attacks: number | null;
  won: number | null;
  /** Energy those attacks cost (Torn's 25 each). */
  energy: number | null;
  perHit: number | null;
  per100Energy: number | null;
  /** Treats per Torn day with any. */
  perDay: number | null;
}

/**
 * Energy to treats, from your own numbers: the treats your log shows in the range, and (Full) your attacks in the same
 * range from Torn's record of your fights — treats per hit and per 100 energy. Nothing is assumed about Torn's rules.
 */
export function treatRates(lines: readonly EventLine[], attacks: ReadonlyArray<{ at: number; won: boolean }> | null, r: DateRange): TreatRates {
  const inside = (at: number) => (r.from === null || at >= r.from) && (r.to === null || at <= r.to);
  const mine = lines.filter((l) => inside(l.at) && l.treats !== null);
  const treats = mine.reduce((a, l) => a + (l.treats ?? 0), 0);
  const days = new Set(mine.filter((l) => (l.treats ?? 0) > 0).map((l) => Math.floor(l.at / 86_400_000))).size;
  const hits = attacks ? attacks.filter((a) => inside(a.at)) : null;
  const n = hits ? hits.length : null;
  const energy = n === null ? null : n * EVENT_ATTACK_ENERGY;
  return {
    treats,
    attacks: n,
    won: hits ? hits.filter((a) => a.won).length : null,
    energy,
    perHit: n ? treats / n : null,
    per100Energy: energy ? (treats / energy) * 100 : null,
    perDay: days ? treats / days : null,
  };
}

// ---------------------------------------------------------------- Torn's calendar

export interface CalendarEntry {
  title: string;
  /** ms */
  start: number;
  end: number;
}

/** Torn's calendar answer (v2 `torn/calendar`: `calendar.events` and `calendar.competitions`) → its entries (ms). */
export function readTornCalendar(body: unknown): CalendarEntry[] {
  const cal = obj(obj(body)?.calendar);
  const list = [...(Array.isArray(cal?.events) ? cal!.events : []), ...(Array.isArray(cal?.competitions) ? cal!.competitions : [])] as unknown[];
  return list
    .map((x) => {
      const e = obj(x) ?? {};
      return { title: String(e.title ?? e.name ?? ''), start: Number(e.start) * 1000, end: Number(e.end) * 1000 };
    })
    .filter((e) => e.title && Number.isFinite(e.start) && Number.isFinite(e.end) && e.end > e.start);
}

/**
 * An event's dates in Torn's calendar: the one on now, else the next one, else the latest past one. `on`: running now;
 * the range covers its whole days (TCT).
 */
export function eventWindow(calendar: readonly CalendarEntry[], id: TornEvent, now: number): { entry: CalendarEntry; on: boolean; range: DateRange } | null {
  const re = eventInfo(id).calendar;
  const mine = calendar.filter((c) => re.test(c.title)).sort((a, b) => a.start - b.start);
  const pick = mine.find((c) => c.start <= now && now < c.end) ?? mine.find((c) => c.start > now) ?? mine.filter((c) => c.end <= now).at(-1);
  if (!pick) return null;
  const day = 86_400_000;
  return { entry: pick, on: pick.start <= now && now < pick.end, range: { from: Math.floor(pick.start / day) * day, to: Math.ceil(pick.end / day) * day - 1 } };
}
